import entries from "lodash/entries";
import flatMap from "lodash/flatMap";
import groupBy from "lodash/groupBy";
import isEmpty from "lodash/isEmpty";
import { TemplateKey } from "PFApp/constants/templates";
import { PER_PAGE_UNPAGINATED } from "PFApp/constants/unpaginated";
import { getVisibleProperties } from "PFCore/helpers/templates";
import { AutocompleteCustomValuesParams } from "PFCore/services/autocomplete/fetch_autocomplete_custom_values";
import { Subtemplate } from "PFCore/types";
import { useCallback, useMemo } from "react";

import { useManyCustomValuesOptions } from "../custom_values/use_custom_values_options";
import { useSubtemplates } from "./use_subtemplates";

type UseSubtemplatesDefaultAPICustomValues = {
  templateKey: TemplateKey;
  enabled?: boolean;
};

/*
  The account may have a lot of subtemplates, each with custom fields having default values.
  To avoid making a request per custom field in a role (a lot of roles in "Apply blueprint" page),
  this hook is meant to query all default custom values from all subtemplates of a given template at once.
*/
export const useSubtemplatesDefaultAPICustomValues = ({
  templateKey,
  enabled
}: UseSubtemplatesDefaultAPICustomValues) => {
  const { data: subtemplates = [], isFetched: isSubtemplatesFetched } = useSubtemplates(
    { templateKey },
    { enabled: enabled ?? true }
  );

  const allSubtemplatesDefaultValues = flatMap<Subtemplate, AutocompleteCustomValuesParams>(
    subtemplates,
    (subtemplate) => {
      const visibleProperties = getVisibleProperties(subtemplate);

      return visibleProperties
        .filter((property) => property.type === "custom_field")
        .filter((property) => !!property.values?.length)
        .map((property) => ({
          type: property.name
        }));
    }
  );

  // Deduplicate by type — one request per custom type, fetching all values.
  // We do NOT pass `value` (text strings from subtemplate config) to the endpoint because
  // autocomplete/custom_values filters `value` by integer ID, not by text.
  // getAPICustomValuesByText handles the text-based match client-side.
  const paramsList = entries(groupBy(allSubtemplatesDefaultValues, "type")).map(([type]) => ({
    type,
    limit: PER_PAGE_UNPAGINATED
  }));

  const queryResult = useManyCustomValuesOptions(paramsList);

  const customValuesData = useMemo(() => flatMap(queryResult, ({ data }) => data || []), [queryResult]);

  const getAPICustomValuesByText = useCallback(
    (customTypeName: string, textValues: string[]) => {
      const customValues = customValuesData.filter(
        (customValue) =>
          customValue.type === customTypeName &&
          typeof customValue.text === "string" &&
          textValues.includes(customValue.text)
      );
      return isEmpty(customValues) ? null : customValues;
    },
    [customValuesData]
  );

  const isFetched = isSubtemplatesFetched && queryResult.every(({ isFetched }) => isFetched);

  return {
    getAPICustomValuesByText,
    isFetched
  };
};
